/**
 * Sidebar.tsx — Ghost-CMS-Style Navigation
 *
 * Ersetzt die alte NavRail.
 * Zwei Zustände:
 * 1. Collapsed (~60px): Nur Icons, vertikal gestapelt
 * 2. Expanded (~280px): Icons + Labels, Sub-Items, Badges
 *
 * Expanded sobald ein Menüpunkt aktiv ist.
 * ContentPanel bleibt als separates Element rechts daneben.
 */

import React, { useCallback } from 'react'
import { useAuthStore } from '../../store/auth'
import {
  Files,
  FileText,
  ClipboardCheck,
  StickyNote,
  Bot,
  Leaf,
  Focus,
  Grape,
  LayoutDashboard,
  Columns3,
  Menu,
  Table2,
  Upload,
  Gamepad2,
} from 'lucide-react'

/* ── Re-export NavSection (same type as old NavRail) ───────────────────────── */

export type NavSection =
  | 'gebaeude'
  | 'raumbuch'
  | 'reports'
  | 'docs'
  | 'audits'
  | 'notes'
  | 'pointcloud-upload'
  | 'controller'
  | 'agent'
  | 'emmissionssenken'
  | 'scene'
  | 'punktwolke'
  | 'whiteboard'
  | 'board'
  | 'user'

/** Sections that render as an independent overlay (not replacing the main panel) */
export const OVERLAY_SECTIONS: ReadonlySet<NavSection> = new Set(['scene', 'punktwolke'])

/* ── Icon config ────────────────────────────────────────────────────────────── */

const ICON_PROPS = { size: 20, strokeWidth: 1.5, color: 'currentColor' } as const

/** Gebäude icon — custom SVG (default / active variants) */
const IconGebaeude = (active: boolean) => (
  <svg viewBox="0 0 24 24" width="20" height="20">
    <path
      d="m21.12 6.4-6.05-4.06a2 2 0 0 0-2.17-.05L2.95 8.41a2 2 0 0 0-.95 1.7v5.82a2 2 0 0 0 .88 1.66l6.05 4.07a2 2 0 0 0 2.17.05l9.95-6.12a2 2 0 0 0 .95-1.7V8.06a2 2 0 0 0-.88-1.66Z"
      fill={active ? '#1a1a1a' : '#ffffff'}
      stroke={active ? '#ffffff' : 'currentColor'}
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
    />
    <path d="M10 22v-8L2.25 9.15" stroke={active ? '#ffffff' : 'currentColor'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <path d="m10 14 11.77-6.87" stroke={active ? '#ffffff' : 'currentColor'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
)

/* ── Menu structure ─────────────────────────────────────────────────────────── */

interface MenuItem {
  id: NavSection
  label: string
  icon: React.ReactNode
  activeIcon?: React.ReactNode
  isOverlay?: boolean
  /** Permission-Key für Modul-Zugriff (z.B. 'module:gebaeude'). Ohne = immer sichtbar. */
  moduleKey?: string
}

interface MenuGroup {
  items: MenuItem[]
  separator?: boolean
  pushBottom?: boolean
}

const MENU: MenuGroup[] = [
  {
    items: [
      { id: 'gebaeude', label: 'Gebäude', icon: IconGebaeude(false), activeIcon: IconGebaeude(true), moduleKey: 'module:gebaeude' },
      { id: 'raumbuch', label: 'Raumbuch', icon: <Table2 {...ICON_PROPS} />, moduleKey: 'module:raumbuch' },
    ],
  },
  {
    separator: true,
    items: [
      { id: 'whiteboard', label: 'Whiteboard', icon: <LayoutDashboard {...ICON_PROPS} />, moduleKey: 'module:whiteboard' },
      { id: 'board',      label: 'Board',      icon: <Columns3 {...ICON_PROPS} />, moduleKey: 'module:whiteboard' },
      { id: 'reports',    label: 'Reports',    icon: <FileText {...ICON_PROPS} />, moduleKey: 'module:reports' },
    ],
  },
  {
    separator: true,
    items: [
      { id: 'docs',   label: 'Docs',   icon: <Files {...ICON_PROPS} />, moduleKey: 'module:docs' },
      { id: 'audits', label: 'Audits', icon: <ClipboardCheck {...ICON_PROPS} />, moduleKey: 'module:audits' },
      { id: 'notes',  label: 'Notes',  icon: <StickyNote {...ICON_PROPS} />, moduleKey: 'module:notes' },
      { id: 'pointcloud-upload', label: 'PointCloud Upload', icon: <Upload {...ICON_PROPS} />, moduleKey: 'module:clouds' },
      { id: 'controller',        label: 'Controller',        icon: <Gamepad2 {...ICON_PROPS} />, moduleKey: 'module:clouds' },
    ],
  },
  {
    separator: true,
    items: [
      { id: 'emmissionssenken', label: 'CO₂',   icon: <Leaf {...ICON_PROPS} />, moduleKey: 'module:co2' },
    ],
  },
  {
    separator: true,
    pushBottom: true,
    items: [
      { id: 'scene',      label: 'Scene',  icon: <Focus {...ICON_PROPS} />, isOverlay: true, moduleKey: 'module:scenes' },
      { id: 'punktwolke', label: 'Render', icon: <Grape {...ICON_PROPS} />, isOverlay: true, moduleKey: 'module:clouds' },
    ],
  },
]

/* ── All items flat (for collapsed view) ────────────────────────────────────── */

interface CollapsedItem {
  id: NavSection | 'search'
  icon: React.ReactNode
  activeIcon?: React.ReactNode
  separator?: boolean
  isOverlay?: boolean
  moduleKey?: string
}

const COLLAPSED_ICONS: CollapsedItem[] = [
  { id: 'gebaeude',  icon: IconGebaeude(false), activeIcon: IconGebaeude(true), moduleKey: 'module:gebaeude' },
  { id: 'raumbuch',  icon: <Table2 {...ICON_PROPS} />, moduleKey: 'module:raumbuch' },
  { id: 'whiteboard', icon: <LayoutDashboard {...ICON_PROPS} />, separator: true, moduleKey: 'module:whiteboard' },
  { id: 'board',      icon: <Columns3 {...ICON_PROPS} />, moduleKey: 'module:whiteboard' },
  { id: 'reports',    icon: <FileText {...ICON_PROPS} />, moduleKey: 'module:reports' },
  { id: 'docs',       icon: <Files {...ICON_PROPS} />, separator: true, moduleKey: 'module:docs' },
  { id: 'audits',     icon: <ClipboardCheck {...ICON_PROPS} />, moduleKey: 'module:audits' },
  { id: 'notes',      icon: <StickyNote {...ICON_PROPS} />, moduleKey: 'module:notes' },
  { id: 'pointcloud-upload', icon: <Upload {...ICON_PROPS} />, moduleKey: 'module:clouds' },
  { id: 'controller',        icon: <Gamepad2 {...ICON_PROPS} />, moduleKey: 'module:clouds' },
  { id: 'emmissionssenken', icon: <Leaf {...ICON_PROPS} />, separator: true, moduleKey: 'module:co2' },
]

const COLLAPSED_BOTTOM: CollapsedItem[] = [
  { id: 'scene',      icon: <Focus {...ICON_PROPS} />, isOverlay: true, moduleKey: 'module:scenes' },
  { id: 'punktwolke', icon: <Grape {...ICON_PROPS} />, isOverlay: true, moduleKey: 'module:clouds' },
]

// ── Permission-Mapping: NavSection → moduleKey ──────────────────────────────
export const SECTION_MODULE_MAP: Record<string, string> = {
  gebaeude: 'module:gebaeude', raumbuch: 'module:raumbuch',
  whiteboard: 'module:whiteboard', reports: 'module:reports',
  docs: 'module:docs', audits: 'module:audits', notes: 'module:notes',
  'pointcloud-upload': 'module:clouds',
  controller: 'module:clouds',
  emmissionssenken: 'module:co2', agent: 'module:co2',
  scene: 'module:scenes', punktwolke: 'module:clouds',
}

/**
 * Prüft ob der User Zugriff auf ein Modul hat.
 *
 * Permission-Hierarchie:
 *   view  — Modul in Sidebar sichtbar, kann geöffnet werden (nur ansehen)
 *   read  — Daten lesen (impliziert view)
 *   write — Daten erstellen/bearbeiten (impliziert read + view)
 */
export function hasModuleAccess(moduleKey: string | undefined, permissions: string[]): boolean {
  if (!moduleKey) return true // Kein moduleKey = immer sichtbar
  // Admin hat immer Zugriff
  const user = useAuthStore.getState().user
  if (user?.roles?.some((r: any) => r.name === 'Admin')) return true
  // view, read oder write reicht um das Modul zu sehen
  return permissions.includes(`${moduleKey}:view`)
    || permissions.includes(`${moduleKey}:read`)
    || permissions.includes(`${moduleKey}:write`)
}

/** Prüft ob der User Schreibrechte für ein Modul hat */
export function hasModuleWrite(moduleKey: string | undefined, permissions: string[]): boolean {
  if (!moduleKey) return true
  const user = useAuthStore.getState().user
  if (user?.roles?.some((r: any) => r.name === 'Admin')) return true
  return permissions.includes(`${moduleKey}:write`)
}

/** Prüft ob der User Leserechte für ein Modul hat */
export function hasModuleRead(moduleKey: string | undefined, permissions: string[]): boolean {
  if (!moduleKey) return true
  const user = useAuthStore.getState().user
  if (user?.roles?.some((r: any) => r.name === 'Admin')) return true
  return permissions.includes(`${moduleKey}:read`) || permissions.includes(`${moduleKey}:write`)
}

/* ── Props ──────────────────────────────────────────────────────────────────── */

interface SidebarProps {
  activeSection: NavSection | null
  overlaySection: NavSection | null
  onSectionChange: (section: NavSection | null) => void
  onOverlayChange: (section: NavSection | null) => void
  onBurgerClick: () => void
}

/* ── Component ──────────────────────────────────────────────────────────────── */

export function Sidebar({
  activeSection,
  overlaySection,
  onSectionChange,
  onOverlayChange,
  onBurgerClick,
}: SidebarProps) {
  const userPermissions = useAuthStore(s => s.user?.permissions || []) as string[]

  // Items filtern basierend auf Modul-Permissions
  const visibleIcons = COLLAPSED_ICONS.filter(item => hasModuleAccess(item.moduleKey, userPermissions))
  const visibleBottom = COLLAPSED_BOTTOM.filter(item => hasModuleAccess(item.moduleKey, userPermissions))

  const handleClick = useCallback((id: NavSection, isOverlay?: boolean) => {
    if (isOverlay) {
      onOverlayChange(overlaySection === id ? null : id)
    } else {
      onSectionChange(activeSection === id ? null : id)
    }
  }, [activeSection, overlaySection, onSectionChange, onOverlayChange])

  const isActive = (id: NavSection, isOverlay?: boolean) =>
    isOverlay ? overlaySection === id : activeSection === id

  /* ── Immer collapsed — Labels nur als Hover-Tooltip ─────────────────────── */
  return (
    <nav className="sidebar sidebar--collapsed" data-component="Sidebar">
      <div className="sidebar__icons">
        <button
          className="sidebar__icon-btn sidebar__icon-btn--back"
          data-label="Projektübersicht"
          onClick={() => {
            localStorage.removeItem('currentProjectId')
            useAuthStore.setState({ currentProjectId: null, currentProject: null })
          }}
        >
          <Menu {...ICON_PROPS} />
        </button>
        <div className="sidebar__separator" />
        {visibleIcons.map((item, i) => {
          const active = item.id !== 'search' && isActive(item.id as NavSection, item.isOverlay)
          const label = item.id === 'search' ? 'Suche (⌘K)' : MENU.flatMap(g => g.items).find(m => m.id === item.id)?.label ?? item.id
          return (
            <React.Fragment key={item.id + '-' + i}>
              {item.separator && <div className="sidebar__separator" />}
              <button
                className={`sidebar__icon-btn${active ? ' sidebar__icon-btn--active' : ''}`}
                data-label={label}
                onClick={() => {
                  if (item.id === 'search') {
                    onBurgerClick()
                  } else {
                    handleClick(item.id as NavSection, item.isOverlay)
                  }
                }}
              >
                {active && item.activeIcon ? item.activeIcon : item.icon}
              </button>
            </React.Fragment>
          )
        })}
      </div>

      <div className="sidebar__icons sidebar__icons--bottom">
        <div className="sidebar__separator" />
        {visibleBottom.map(item => {
          const active = isActive(item.id as NavSection, item.isOverlay)
          const label = MENU.flatMap(g => g.items).find(m => m.id === item.id)?.label ?? item.id
          return (
            <button
              key={item.id}
              className={`sidebar__icon-btn${active ? ' sidebar__icon-btn--active' : ''}`}
              data-label={label}
              onClick={() => handleClick(item.id as NavSection, item.isOverlay)}
            >
              {active && item.activeIcon ? item.activeIcon : item.icon}
            </button>
          )
        })}
      </div>
    </nav>
  )
}
